/**
 * Stage 3b.1: UI timeline for the open chat comes from core.messages,
 * not from a mirrored copy on activeChat.chat.messages.
 */
import { computed, type ComputedRef } from 'vue'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { ChatTransformer } from '@/modules/chat-matrix-fast/services'
import type { ChatMessage } from '@/modules/chat-matrix-fast/types'
import type { IChat, IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { sameChatId } from '@/modules/chat-matrix-fast/utils/topics'

/** True when core.messages belong to the currently open UI chat (incl. topics). */
export function isCoreTimelineForActiveChat(
  activeChatId: number | string | null | undefined,
  core: {
    currentChat: IChat | null
    activeTopic: unknown
  },
): boolean {
  if (activeChatId == null) {
    return false
  }

  // Topics: currentChat = parent chat id (same as activeChat.chat.id).
  // Пока currentChat отстаёт от activeChat — пустой timeline (не чужие сообщения).
  return sameChatId(core.currentChat?.id, activeChatId)
}

export function resolveActiveChatUiMessages(
  activeChatId: number | string | null | undefined,
  core: {
    currentChat: IChat | null
    activeTopic: unknown
    messages: IMessage[]
  },
): ChatMessage[] {
  if (!isCoreTimelineForActiveChat(activeChatId, core)) {
    return []
  }

  return ChatTransformer.toUiMessages(core.messages)
}

export function useActiveChatMessages(): ComputedRef<ChatMessage[]> {
  const store = useChatStore()
  const chatStoreCore = useChatStoreCore()

  return computed(() => resolveActiveChatUiMessages(
    store.activeChat?.chat?.id,
    {
      currentChat: chatStoreCore.currentChat,
      activeTopic: chatStoreCore.activeTopic,
      messages: chatStoreCore.messages,
    },
  ))
}
